<script setup>
import { computed } from 'vue';

import { HrIconBtn } from '../primitives';

/**
 * Icon button used as the trigger for a contextual or overflow menu.
 *
 * @status recommended
 * @group actions
 * @useWhen Opening a dropdown menu; typically paired with HrAdaptiveMenu or HrMenu
 * @related HrOverflowMenuBtn
 * @related HrIconActionBtn
 * @a11y Icon-only — callers should supply aria-label (e.g. "More options") or aria-haspopup on the wrapper.
 */
defineOptions({ name: 'HrIconMenuBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /** Material icon name. */
  icon: { type: String, default: 'more_vert' },
  /** Shows a loading spinner in place of the icon. */
  loading: { type: Boolean, default: false },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: { type: Boolean, default: false },
  /** Renders a skeleton placeholder instead of the button. */
  pending: { type: Boolean, default: false },
  /**
   * Size of the button container and icon.
   * @values xs, sm, md, lg, xl
   */
  size: { type: String, default: 'md' },
  /** Vue Router route location. Renders the button as a router-link. */
  to: { type: [String, Object], default: null },
  /** Replace the current history entry instead of pushing a new one. */
  replace: { type: Boolean, default: false },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: { type: String, default: null },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: { type: String, default: null },
  /**
   * Quasar visual props forwarded to the underlying icon button. Ignored in the design-system theme.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: { type: Object, default: () => ({}) },
});

// @todo Remove legacy-props when Legacy theme is removed
const mergedLegacyProps = computed(() => ({
  flat: true,
  color: 'dark',
  ...props.legacyProps,
}));
</script>

<template>
  <hr-icon-btn
    variant="standard"
    v-bind="$props"
    :legacy-props="mergedLegacyProps"
    @click="emit('click', $event)"
  >
    <!-- @slot Menu or overlay placed inside the trigger — typically a HrMenu or HrAdaptiveMenu. -->
    <slot />
  </hr-icon-btn>
</template>
